import { Panel } from "@poseidon/ui/components/Panel";
import { Button } from "@mui/material";
import { useApplicationCheckList } from "../hooks/useApplicationCheckList";
import LinearProgress from "@mui/material/LinearProgress";
import { useConvertToAgreement } from "../../Applications/hooks/useConvertToAgreement";
import { ConvertToAgreementDialog } from "../../Applications/components/ConvertToAgreementDialog";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { useState } from "react";
import DoneIcon from "@mui/icons-material/Done";
import { useDoc } from "@poseidon/firebase";
import { isApplicationEditable } from "../../Applications/isApplicationEditable";
import ErrorBoundary from "@poseidon/ui/components/ErrorBoundary";
import {
  CheckListActionRow,
  CheckListItemRow,
  CheckListTable,
} from "@poseidon/ui/components/CheckListTable";
import {
  SailorCheckListItemIdVerified,
  SailorCheckListItemIncomeDetails,
  SailorCheckListItemKycAmlCheckDone,
  SailorCheckListItemKycAmlConsent,
  SailorCheckListItemOpenBankingConsent,
  SailorCheckListItemPersonalDetails,
  SailorCheckListItemSupportingDocumentation,
} from "./SailorCheckListItems";
import { useSailorSubmitForChecks } from "../../Applications/hooks/useSailorSubmitForChecks";

function CheckListTableWired({ application, data }) {
  const applicationId = application.id;
  const convertToAgreement = useConvertToAgreement(applicationId);
  const sailorSubmitForChecks = useSailorSubmitForChecks(applicationId);
  const readonly = !isApplicationEditable(application);
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const [saving, setSaving] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const { update: updateApplication } = useDoc("applications", applicationId);
  const handleConvertToAgreement = (options) => {
    setSaving(true);
    convertToAgreement.convertToAgreement(options).then((success) => {
      setConvertOpen(false);
      if (success) {
        setToast("Application has been converted to an Agreement");
      } else {
        setError("Error converting Application to an Agreement");
      }
      setSaving(false);
    });
  };
  const handleLockPersonalDetails = () => {
    setSaving(true);
    sailorSubmitForChecks.sailorSubmitForChecks().then((success) => {
      if (success) {
        setToast("Personal details have been locked & checks initiated");
      } else {
        setError("Error locking personal details/initiating checks");
      }
      setSaving(false);
    });
    // updateApplication({ lock_personalDetails: true }).then((success) => {
    //   if (success) {
    //     setToast("Personal details have been locked");
    //   } else {
    //     setError("Error locking personal details");
    //   }
    //   setSaving(false);
    // });
  };
  const handleLockApplication = () => {
    setSaving(true);
    updateApplication({ lock_sailor: true, lock_dealer: true }).then(
      (success) => {
        if (success) {
          setToast("Application has been locked");
        } else {
          setError("Error locking application");
        }
        setSaving(false);
      }
    );
  };
  const sailorId = application.applicant.sailor.id;
  const applicantType = "Main";
  const jointApplicantId = application.jointApplicant?.sailor?.id;
  return (
    <CheckListTable>
      <SailorCheckListItemPersonalDetails
        status={data.personalDetails}
        sailorId={sailorId}
        applicationId={applicationId}
        applicantType={applicantType}
      />
      <SailorCheckListItemIncomeDetails
        status={data.incomeDetails}
        applicantType={applicantType}
        sailorId={sailorId}
        applicationId={applicationId}
      />
      <SailorCheckListItemPersonalDetails
        status={data.personalDetailsJointApplicant}
        sailorId={jointApplicantId}
        applicationId={applicationId}
        applicantType="Joint"
      />
      <SailorCheckListItemIncomeDetails
        status={data.incomeDetailsJointApplicant}
        applicantType="Joint"
        sailorId={jointApplicantId}
        applicationId={applicationId}
      />
      <SailorCheckListItemKycAmlConsent
        status={data.kycAmlConsent}
        sailorId={sailorId}
        applicationId={applicationId}
        jointSailorId={jointApplicantId}
      />
      <CheckListItemRow
        status={data.quoteValid}
        label="Quote Valid"
        tooltip="Is the quote valid and trusted?"
        manageLink={`/applications/${applicationId}#quote`}
        manageText="Scroll to quote"
      />
      <CheckListActionRow>
        <Button
          variant="outlined"
          startIcon={
            application.lock_personalDetails ? <DoneIcon /> : undefined
          }
          disabled={
            saving ||
            application.lock_personalDetails ||
            readonly ||
            data.blocks.creditCheck
          }
          onClick={handleLockPersonalDetails}
        >
          Start KYC/AML & Credit Check (Lock Personal Details)
        </Button>
      </CheckListActionRow>
      <SailorCheckListItemKycAmlCheckDone
        status={data.kycAmlCheckDone}
        sailorId={sailorId}
        applicationId={applicationId}
        jointSailorId={application.jointApplicant?.sailor?.id}
      />
      <SailorCheckListItemOpenBankingConsent
        status={data.openBankingConsent}
        sailorId={sailorId}
        applicationId={applicationId}
        jointSailorId={jointApplicantId}
      />
      <SailorCheckListItemSupportingDocumentation
        status={data.supportingDocumentation}
        applicantType={applicantType}
        sailorId={sailorId}
        applicationId={applicationId}
      />
      <SailorCheckListItemSupportingDocumentation
        status={data.supportingDocumentationJointApplicant}
        applicantType="Joint"
        sailorId={jointApplicantId}
        applicationId={applicationId}
      />

      {/* todo: what if it is not verified yet? */}
      <CheckListItemRow
        status={data.boatDealerAssigned}
        label="Boat Dealer Assigned"
        tooltip="Has a dealer been assigned to this application?"
        manageLink={`/applications/${applicationId}#dealer-vessel`}
        manageText="Scroll to dealer"
      />
      <SailorCheckListItemIdVerified
        status={data.idVerified}
        sailorId={sailorId}
        applicationId={applicationId}
        jointSailorId={jointApplicantId}
      />
      <CheckListItemRow
        status={data.affordabilityCheckDone}
        label="Affordability Check Done"
        tooltip="Do either automatic calculations, or manual figures mean the sailor can afford this boat?"
        manageLink={`/applications/${applicationId}/creditUnderwriting`}
        manageText="Go to credit underwriting"
      />
      <CheckListItemRow
        status={data.creditApproved}
        label="Credit Approved"
        manageLink={`/applications/${applicationId}/creditUnderwriting`}
        manageText="Go to credit underwriting"
      />
      <CheckListItemRow
        status={data.surveySupplied}
        label="Survey Supplied"
        manageLink={`/applications/${applicationId}/survey/files`}
        manageText="Go to files"
      />
      <CheckListItemRow
        status={data.invoiceSupplied}
        label="Invoice Supplied"
        manageLink={`/applications/${applicationId}/invoices/files`}
        manageText="Go to files"
      />
      <CheckListItemRow
        status={data.insuranceSupplied}
        label="Insurance Supplied"
        manageLink={`/applications/${applicationId}/insurance/files`}
        manageText="Go to files"
      />
      <CheckListItemRow
        status={data.boatDocumentationSupplied}
        label="Boat Documentation Supplied"
        manageLink={`/boats/${application.vessel?.id}`}
        manageText="Go to boat"
      />
      <CheckListItemRow
        status={data.boatSpecificationUpdated}
        label="Boat Specification Updated"
        manageLink={`/boats/${application.vessel?.id}`}
        manageText="Go to boat"
      />
      <CheckListActionRow>
        <Button
          variant="outlined"
          startIcon={
            application.lock_sailor && application.lock_dealer ? (
              <DoneIcon />
            ) : undefined
          }
          disabled={
            saving ||
            readonly ||
            data.blocks.documentGeneration ||
            (application.lock_sailor && application.lock_dealer)
          }
          onClick={handleLockApplication}
        >
          Start Document Generation (Lock Application)
        </Button>
      </CheckListActionRow>
      <CheckListItemRow
        status={data.loanDocumentsGenerated}
        label="Loan Documents Generated"
      />
      <CheckListItemRow
        status={data.completionDateSet}
        label="Completion Date Set"
        manageLink={`/applications/${applicationId}#completion-date`}
        manageText="Scroll to Completion Date"
      />
      <CheckListItemRow
        status={data.mcaRegistration}
        label="MCA Registration Details"
        manageLink={`/applications/${applicationId}#mca-details`}
        manageText="Scroll to MCA details"
      />
      <CheckListItemRow
        status={data.documentsSigned}
        label="Documents Signed"
        manageLink={`/applications/${applicationId}/loanDocs/files`}
        manageText="Go to files"
      />
      <CheckListActionRow>
        <Button
          variant="outlined"
          startIcon={readonly ? <DoneIcon /> : undefined}
          disabled={saving || readonly || data.blocks.convertToAgreement}
          onClick={() => setConvertOpen(true)}
        >
          Convert to agreement
        </Button>
      </CheckListActionRow>
      <ConvertToAgreementDialog
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        onConfirm={handleConvertToAgreement}
        application={application}
        saving={saving}
      />
    </CheckListTable>
  );
}

export const ApplicationCheckList = ({ application }) => {
  const { loading, checklist: data } = useApplicationCheckList(application);
  return (
    <Panel title="Check List" expandable>
      {loading ? (
        <LinearProgress />
      ) : (
        <ErrorBoundary>
          <CheckListTableWired data={data} application={application} />
        </ErrorBoundary>
      )}
    </Panel>
  );
};
